import React, { useState, useEffect } from 'react';
import { aiV2API } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * Reusable AI model/provider selector.
 * Props:
 *   value: { provider, model } | null
 *   onChange: ({ provider, model }) => void
 *   compact: boolean (show only icon in compact mode)
 */
export function AIModelSelector({ value, onChange, compact = false }) {
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    aiV2API.providers()
      .then(r => setProviders(r.data.providers || []))
      .catch(() => setProviders([{ id: 'default', name: 'Neural News AI', models: ['gpt-4.1-mini'] }]));
  }, []);

  if (!providers.length) return null;

  const activeProvider = providers.find(p => p.id === (value?.provider || providers[0]?.id)) || providers[0];
  const providerModels = activeProvider?.models || [];

  const handleProvider = (pid) => {
    const prov = providers.find(p => p.id === pid);
    onChange({ provider: pid, model: prov?.models[0] || '' });
  };

  const handleModel = (m) => {
    onChange({ ...(value || {}), provider: activeProvider?.id, model: m });
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-[#5B616E] bg-[#FBF7EF] border border-[#E7E0D6] rounded px-1.5 py-0.5 font-mono">
          {activeProvider?.name} / {value?.model || providerModels[0] || 'default'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={value?.provider || activeProvider?.id} onValueChange={handleProvider}>
        <SelectTrigger className="w-40 h-7 text-xs">
          <SelectValue placeholder="Provider" />
        </SelectTrigger>
        <SelectContent>
          {providers.map(p => (
            <SelectItem key={p.id} value={p.id} disabled={p.id === 'none'}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {providerModels.length > 0 && (
        <Select value={value?.model || providerModels[0]} onValueChange={handleModel}>
          <SelectTrigger className="w-48 h-7 text-xs">
            <SelectValue placeholder="Model" />
          </SelectTrigger>
          <SelectContent>
            {providerModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
